import React, { useEffect, useState } from 'react';
import "../css/LogPage.css";

const LogPage = () => {
    // États pour stocker les logs, les logs filtrés, l'état de chargement, la recherche et tri
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchColumn, setSearchColumn] = useState(''); // Colonne choisie pour filtrer
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const API_URL = process.env.REACT_APP_API_URL;

    // Liste des colonnes disponibles dans les logs (à afficher dans le tableau)
    const columnKeys = [
        'id', 'message', 'niveau', 'created_at', 'email',
        'action', 'donnees', 'source', 'dataroom_id', 'authorization'
    ];

    // useEffect pour récupérer les logs depuis l'API au chargement du composant
    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await fetch(`${API_URL}/controllers/getLogs.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include', // envoie les cookies avec la requête
                    body: JSON.stringify({
                        dossierId: localStorage.getItem('dossierId'), // envoi l'id du dossier
                    }),
                });

                const data = await response.json();
                setLogs(data);         // Stocke tous les logs reçus
                setFilteredLogs(data); // Initialise la liste filtrée avec tous les logs
            } catch (err) {
                console.error("Erreur lors de récupération des logs :", err);
            } finally {
                setLoading(false); // Stoppe le chargement, même en cas d'erreur
            }
        };

        fetchLogs();
    }, []);

    // useEffect pour filtrer les logs selon la recherche et la colonne sélectionnée
    useEffect(() => {
        const lowerSearch = search.toLowerCase();

        // Si aucune colonne sélectionnée, on remet la liste filtrée à la liste complète
        if (!searchColumn) {
            setFilteredLogs(logs);
            return;
        }

        // Filtre les logs où la valeur dans la colonne sélectionnée contient la recherche
        setFilteredLogs(
            logs.filter((log) =>
                String(log[searchColumn]).toLowerCase().includes(lowerSearch)
            )
        );
    }, [search, searchColumn, logs]);

    // Fonction pour trier les logs par une colonne donnée
    const sortBy = (key) => {
        let direction = 'asc';

        // Si on trie déjà par cette colonne en ascendant, on inverse la direction
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        // Trie une copie des logs filtrés selon la clé et la direction
        const sorted = [...filteredLogs].sort((a, b) => {
            if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        // Met à jour la config de tri et la liste filtrée triée
        setSortConfig({ key, direction });
        setFilteredLogs(sorted);
    };

    // Affiche un message de chargement pendant la récupération des données
    if (loading) return <p className="p-4">Chargement des logs...</p>;

    // JSX pour afficher la page de logs
    return (
        <div className="log-page-container">
            <h1 className="page-title">Logs</h1>

            {/* Zone de recherche avancée */}
            <div className="search-container">
                <select
                    value={searchColumn}
                    onChange={(e) => setSearchColumn(e.target.value)}
                    className="search-select"
                >
                    <option value="">-- Sélectionner une colonne --</option>
                    {columnKeys.map((key) => (
                        <option key={key} value={key}>{key}</option>
                    ))}
                </select>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher dans la colonne sélectionnée..."
                    className="search-input"
                />
            </div>

            {/* Tableau des logs */}
            <div className="table-wrapper">
                <table className="logs-table">
                    <thead>
                    <tr>
                        {columnKeys.map((key) => (
                            <th
                                key={key}
                                onClick={() => sortBy(key)}   // Tri au clic sur l'en-tête
                                className="table-header"
                            >
                                {key.toUpperCase()}
                                {/* Flèche indiquant l'ordre du tri */}
                                {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽') : ''}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {/* Affichage des logs filtrés */}
                    {filteredLogs.map((log) => (
                        <tr key={log.id}>
                            {columnKeys.map((key) => (
                                <td key={key} className="table-cell">{log[key]}</td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LogPage;